import { useRef, useState } from 'react'
import { Check, Cross, Laptop, Phone, Shield, Unplugged, Upload } from '../icons'
import { LINK_FILE_ACCEPT } from '../link/file'
import type { LinkStatus } from '../link/useLink'

/**
 * The front of the private portal, and the surface the whole feature is judged on.
 *
 * It has two shapes, and which one she sees is decided by where the page came from rather than by
 * anything she picks. Served by her phone, the only thing left to do is type the six digits on its
 * screen, so that is the whole surface. Served by covellah.com, the phone is not on the other end
 * of this connection at all and the file door is the only honest offer.
 *
 * Not a modal. Nothing is behind it, there is nothing to return to, and a dialog over an empty
 * dashboard would be interrupting a task nobody had started.
 */
export function PairingScreen({
  status,
  servedByPhone,
  onCode,
  onFile,
}: {
  status: LinkStatus
  servedByPhone: boolean | null
  onCode: (code: string) => void
  onFile: (file: File, passphrase?: string) => void
}) {
  const [code, setCode] = useState('')
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement | null>(null)

  const takeFile = (list: FileList | null) => {
    const file = list?.[0]
    if (file) onFile(file)
  }

  const working = status.kind === 'preparing'
  const lockedOut = status.kind === 'error' && !status.retryable

  return (
    <div className="pairing">
      <header className="pairing-head">
        <h1 className="pairing-title">Your phone, on this screen</h1>
        <p className="pairing-lede">
          Covellah keeps your cycle, your counts and your shelf on your phone and nowhere else. To
          read them on a larger screen, your phone hands them to this tab directly. They are drawn
          from memory and forgotten when you close it.
        </p>
        <ul className="pairing-facts">
          <li>
            <Unplugged size={16} />
            No account, no upload, no copy kept
          </li>
          <li>
            <Shield size={16} />
            Never leaves your own network
          </li>
          <li>
            <Laptop size={16} />
            Closing this tab is the delete button
          </li>
        </ul>
      </header>

      {servedByPhone === null ? (
        <p className="door-note">One moment.</p>
      ) : servedByPhone ? (
        /* ------------------------------------------------------ served by the phone */
        <section className="door door-solo" aria-labelledby="door-code">
          <div className="door-head">
            <span className="door-index">Connected</span>
            <h2 className="door-title" id="door-code">
              Type the code on your phone
            </h2>
            <p className="door-note">
              This page came from your phone over your own Wi-Fi. The six digits are how it knows
              the laptop asking is yours.
            </p>
          </div>

          <form
            className="code-form"
            onSubmit={(event) => {
              event.preventDefault()
              if (code.length === 6) onCode(code)
            }}
          >
            <label className="console-label" htmlFor="pairing-code">
              Code
            </label>
            <input
              id="pairing-code"
              className="code-input"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              placeholder="000000"
              disabled={working || lockedOut}
              aria-describedby={status.kind === 'error' ? 'code-error' : undefined}
            />
            <button
              type="submit"
              className="cbtn cbtn-primary"
              disabled={code.length !== 6 || working || lockedOut}
            >
              <Phone size={16} />
              {working ? 'Reading your phone' : 'Show me my day'}
            </button>
          </form>

          {status.kind === 'error' ? (
            <p className="door-error" id="code-error" role="alert">
              <Cross size={16} />
              {status.message}
            </p>
          ) : null}
        </section>
      ) : (
        /* ------------------------------------------------------ served by covellah.com */
        <section className="door door-solo" aria-labelledby="door-file">
          <div className="door-head">
            <span className="door-index">From a file</span>
            <h2 className="door-title" id="door-file">
              Drop in an export
            </h2>
            <p className="door-note">
              On your phone, open Covellah, then Settings, then Read on my laptop, and send yourself
              the file. Drop it here and it is read by this browser alone.
            </p>
          </div>

          <div
            className={dragging ? 'dropzone dropzone-hot' : 'dropzone'}
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              takeFile(event.dataTransfer.files)
            }}
          >
            <Upload size={30} />
            <p className="dropzone-title">Drop your .covella file here</p>
            <p className="door-note">It is never uploaded.</p>
            <button type="button" className="cbtn cbtn-quiet" onClick={() => fileInput.current?.click()}>
              Choose a file
            </button>
            <input
              ref={fileInput}
              type="file"
              accept={LINK_FILE_ACCEPT}
              className="visually-hidden"
              onChange={(event) => takeFile(event.target.files)}
            />
          </div>

          {status.kind === 'error' ? (
            <p className="door-error" role="alert">
              <Cross size={16} />
              {status.message}
            </p>
          ) : null}

          <p className="door-note">
            Your phone can also show this page directly, with nothing to move and no file to keep.
            Read on my laptop offers that too.
          </p>
        </section>
      )}

      <footer className="pairing-foot">
        <Check size={15} />
        This page has no server to send anything to. The only network address it knows belongs to
        your account and the shared recipe library, and neither is touched while you are here.
      </footer>
    </div>
  )
}
